import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  X,
  MessageSquare,
  ChevronDown,
  Settings,
  Users,
  Send,
  RefreshCw,
  Loader2,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { approvalsAPI, postsAPI, getErrorMessage } from '../../services/api';

type ApprovalTab = 'pending' | 'approved' | 'rejected' | 'all';

interface ApprovalItem {
  id: string;
  content: string;
  submitted_by: {
    name: string;
    user_id: string;
  };
  submitted_time: string;
  platforms: string[];
  media_url?: string | null;
  status: string;
  feedback?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
}

interface ApprovalStats {
  pending: number;
  approved_today: number;
  avg_review_time: number;
  rejection_rate: number;
}

interface DraftPost {
  id: string;
  text: string;
  status: string;
}

const ApprovalsPage: React.FC = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<ApprovalTab>('pending');
  const [expandedSettings, setExpandedSettings] = useState(false);
  const [feedbackInput, setFeedbackInput] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [approvalItems, setApprovalItems] = useState<ApprovalItem[]>([]);
  const [stats, setStats] = useState<ApprovalStats>({ pending: 0, approved_today: 0, avg_review_time: 0, rejection_rate: 0 });
  const [drafts, setDrafts] = useState<DraftPost[]>([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [workflowSettings, setWorkflowSettings] = useState({
    approvalRequired: true,
    requiredApproversCount: 2,
    notifyOnSubmission: true,
    notifyOnApproval: true,
  });

  const tabs: { id: ApprovalTab; label: string; count?: number }[] = [
    { id: 'pending', label: t('approvals_tab_pending', 'Pending Review'), count: stats.pending },
    { id: 'approved', label: t('approvals_tab_approved', 'Approved') },
    { id: 'rejected', label: t('approvals_tab_rejected', 'Rejected') },
    { id: 'all', label: t('approvals_tab_all', 'All') },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        approvalsAPI.list({ status: activeTab !== 'all' ? activeTab : undefined }),
        approvalsAPI.stats(),
      ]);
      setApprovalItems(itemsRes.data.items || []);
      setStats(statsRes.data);
    } catch {
      // API not available — show empty state
      setApprovalItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch drafts for submit modal
  const fetchDrafts = async () => {
    try {
      const res = await postsAPI.list({ status: 'draft' });
      setDrafts((res.data?.posts || []).filter((p: DraftPost) => p.status === 'draft'));
    } catch {
      setDrafts([]);
    }
  };

  const handleSubmitForApproval = async (postId: string) => {
    setActionLoading(postId);
    try {
      await approvalsAPI.submit(postId);
      toast.success(t('approvals_submitted', 'Post submitted for approval!'));
      setShowSubmitModal(false);
      fetchData();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(getErrorMessage(error, t('approvals_submit_failed', 'Failed to submit')));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await approvalsAPI.approve(id, feedbackInput[id] || undefined);
      toast.success(t('approvals_approved', 'Post approved!'));
      setFeedbackInput({ ...feedbackInput, [id]: '' });
      fetchData();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(getErrorMessage(error, t('approvals_approve_failed', 'Failed to approve')));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    if (!window.confirm(t('approvals_confirm_reject', 'Are you sure you want to reject this post?'))) {
      return;
    }
    setActionLoading(id);
    try {
      await approvalsAPI.reject(id, feedbackInput[id] || undefined);
      toast.error(t('approvals_rejected', 'Post rejected. Feedback sent to submitter.'));
      setFeedbackInput({ ...feedbackInput, [id]: '' });
      fetchData();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(getErrorMessage(error, t('approvals_reject_failed', 'Failed to reject')));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestChanges = async (id: string) => {
    if (!feedbackInput[id]) {
      toast.error(t('approvals_feedback_required', 'Please provide feedback before requesting changes'));
      return;
    }
    setActionLoading(id);
    try {
      await approvalsAPI.requestChanges(id, feedbackInput[id]);
      toast.success(t('approvals_feedback_sent', 'Feedback sent. Awaiting revisions...'));
      setFeedbackInput({ ...feedbackInput, [id]: '' });
      fetchData();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(getErrorMessage(error, t('approvals_changes_failed', 'Failed to request changes')));
    } finally {
      setActionLoading(null);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook': return <Facebook size={16} />;
      case 'twitter': return <Twitter size={16} />;
      case 'instagram': return <Instagram size={16} />;
      case 'linkedin': return <Linkedin size={16} />;
      default: return <MessageSquare size={16} />;
    }
  };

  const getPlatformColor = (platform: string): string => {
    switch (platform) {
      case 'facebook': return '#1877F2';
      case 'twitter': return '#000000';
      case 'instagram': return '#E4405F';
      case 'linkedin': return '#0A66C2';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'changes_requested': return 'Changes Requested';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const timeAgo = (isoStr: string): string => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-n-border">
        <div className="flex items-start gap-3 mb-1">
          <span className="text-4xl leading-none mt-0.5">✅</span>
          <h1 className="text-4xl font-bold text-n-text">Approvals</h1>
        </div>
        <p className="text-sm text-n-text-md ml-12">Manage post reviews, feedback, and approval workflows for your team.</p>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Toolbar: tabs + stats + actions */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-4 border-b border-n-border bg-n-card z-10">
          {/* Tabs */}
          <div className="flex items-center gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-n-text border-n-text'
                    : 'text-n-text-md border-transparent hover:text-n-text'
                }`}
              >
                {tab.id !== 'all' && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor:
                        tab.id === 'pending'
                          ? '#cb912f'
                          : tab.id === 'approved'
                          ? '#448361'
                          : tab.id === 'rejected'
                          ? '#d44c47'
                          : '#9b9a97',
                    }}
                  />
                )}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-hover text-n-text-md">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Right Toolbar: Stats + Actions */}
          <div className="flex items-center gap-3">
            {/* Stats badges */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: '#fdecc8', color: '#402c1b' }}>
                {t('approvals_pending_count', '{{count}} pending', { count: stats.pending })}
              </span>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: '#dbeddb', color: '#1c3829' }}>
                {t('approvals_approved_today', '{{count}} today', { count: stats.approved_today })}
              </span>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: '#d3e5ef', color: '#183347' }}>
                {t('approvals_avg_review_time', '{{time}}m avg', { time: stats.avg_review_time })}
              </span>
            </div>

            {/* Actions */}
            <button
              onClick={fetchData}
              className="p-1.5 rounded-n-sm text-n-text-lt hover:bg-n-hover transition-all"
              title={t('approvals_refresh', 'Refresh')}
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={() => { setShowSubmitModal(true); fetchDrafts(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-n-sm text-white text-xs font-bold transition-all"
              style={{ backgroundColor: '#2383e2' }}
            >
              <Send size={14} />
              {t('approvals_submit_post', 'Submit Post')}
            </button>
          </div>
        </div>

        {/* Main layout: list + sidebar */}
        <div className="flex-1 min-h-0 overflow-hidden flex gap-4 px-6 py-4">
          {/* Main Content Card */}
          <div className="flex-1 min-h-0 flex flex-col bg-n-card rounded-n-sm border border-n-border shadow-n overflow-hidden">
            {/* Loading State */}
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Loader2 size={24} className="mb-2 text-n-text-lt animate-spin" />
                <p className="text-xs text-n-text-md">{t('approvals_loading', 'Loading approvals...')}</p>
              </div>
            ) : approvalItems.length > 0 ? (
              <div className="flex-1 overflow-y-auto divide-y divide-n-border">
                {approvalItems.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-n-hover transition-colors">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: '#2383e2' }}
                        >
                          {item.submitted_by.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-n-text">{item.submitted_by.name}</p>
                          <p className="text-[10px] text-n-text-md">{timeAgo(item.submitted_time)}</p>
                        </div>
                      </div>
                      <span
                        className="px-2 py-0.5 rounded-n-xs text-[10px] font-bold"
                        style={{
                          backgroundColor:
                            item.status === 'pending'
                              ? '#fdecc8'
                              : item.status === 'approved'
                              ? '#dbeddb'
                              : item.status === 'rejected'
                              ? '#ffe2dd'
                              : item.status === 'changes_requested'
                              ? '#fdecc8'
                              : '#e3e2e0',
                          color:
                            item.status === 'pending'
                              ? '#402c1b'
                              : item.status === 'approved'
                              ? '#1c3829'
                              : item.status === 'rejected'
                              ? '#5d1715'
                              : item.status === 'changes_requested'
                              ? '#402c1b'
                              : '#32302c',
                        }}
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    </div>

                    {/* Content */}
                    <p className="text-sm text-n-text leading-relaxed mb-3">
                      {item.content || <span className="text-n-text-md italic">{t('approvals_no_content', '(No text content)')}</span>}
                    </p>

                    {/* Media Thumbnail */}
                    {item.media_url && (
                      <img
                        src={item.media_url}
                        alt="Post media"
                        className="w-16 h-16 rounded-n-sm object-cover mb-3"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}

                    {/* Platforms */}
                    {item.platforms && item.platforms.length > 0 && (
                      <div className="flex gap-1 mb-3">
                        {item.platforms.map((platform) => (
                          <div
                            key={platform}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-n-xs text-[10px] font-medium capitalize"
                            style={{ backgroundColor: getPlatformColor(platform) + '20', color: getPlatformColor(platform) }}
                          >
                            {getPlatformIcon(platform)}
                            {platform}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Feedback Display */}
                    {item.feedback && item.status !== 'pending' && (
                      <div className="p-3 bg-n-page rounded-n-sm border border-n-border mb-3">
                        <p className="text-[10px] text-n-text-lt font-semibold mb-1">{t('approvals_feedback', 'Feedback')}:</p>
                        <p className="text-xs text-n-text">{item.feedback}</p>
                      </div>
                    )}

                    {/* Actions for Pending Items */}
                    {item.status === 'pending' && (
                      <>
                        <div className="mb-3">
                          <label htmlFor={`feedback-${item.id}`} className="sr-only">{t('approvals_feedback', 'Feedback')}</label>
                          <textarea
                            id={`feedback-${item.id}`}
                            value={feedbackInput[item.id] || ''}
                            onChange={(e) => setFeedbackInput({ ...feedbackInput, [item.id]: e.target.value })}
                            placeholder={t('approvals_add_feedback', 'Add feedback...')}
                            className="w-full px-2 py-1.5 border border-n-border rounded-n-sm text-xs resize-none bg-n-page text-n-text placeholder:text-n-text-lt focus:outline-none focus:ring-1 focus:ring-n-blue"
                            rows={2}
                            style={{ fontFamily: 'inherit' }}
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(item.id)}
                            disabled={actionLoading === item.id}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-n-sm text-white text-xs font-semibold transition-all disabled:opacity-60"
                            style={{ backgroundColor: '#448361' }}
                          >
                            {actionLoading === item.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            {t('approvals_approve_button', 'Approve')}
                          </button>
                          <button
                            onClick={() => handleRequestChanges(item.id)}
                            disabled={actionLoading === item.id}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-n-sm text-white text-xs font-semibold transition-all disabled:opacity-60"
                            style={{ backgroundColor: '#cb912f' }}
                          >
                            <MessageSquare size={12} />
                            {t('approvals_changes_button', 'Changes')}
                          </button>
                          <button
                            onClick={() => handleReject(item.id)}
                            disabled={actionLoading === item.id}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-n-sm text-white text-xs font-semibold transition-all disabled:opacity-60"
                            style={{ backgroundColor: '#d44c47' }}
                          >
                            <X size={12} />
                            {t('approvals_reject_button', 'Reject')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-n-sm bg-n-hover mb-3">
                  <MessageSquare size={20} className="text-n-text-lt" />
                </div>
                <p className="text-sm font-semibold text-n-text mb-1">
                  {activeTab === 'pending' ? t('approvals_no_pending', 'No pending approvals') : t('approvals_no_items', 'No {{tab}} items', { tab: activeTab })}
                </p>
                <p className="text-xs text-n-text-md">
                  {t('approvals_empty_message', 'Submit a draft post for approval to get started')}
                </p>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-80 min-h-0 flex flex-col gap-4 overflow-y-auto">
            {/* Workflow Settings */}
            <div className="bg-n-card rounded-n-sm border border-n-border shadow-n overflow-hidden flex-shrink-0">
              <button
                onClick={() => setExpandedSettings(!expandedSettings)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-n-hover transition-colors border-b border-n-border"
              >
                <div className="flex items-center gap-2">
                  <Settings size={14} className="text-n-blue" />
                  <span className="text-xs font-semibold text-n-text">{t('approvals_workflow_settings', 'Workflow Settings')}</span>
                </div>
                <ChevronDown size={14} className="text-n-text-lt transition-transform" style={{ transform: expandedSettings ? 'rotate(180deg)' : 'rotate(0)' }} />
              </button>

              {expandedSettings && (
                <div className="px-4 pb-4 pt-3">
                  <div className="mb-3">
                    <label className="flex items-center cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={workflowSettings.approvalRequired}
                        onChange={(e) => setWorkflowSettings({ ...workflowSettings, approvalRequired: e.target.checked })}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-xs text-n-text">{t('approvals_require_approval', 'Require Approval')}</span>
                    </label>
                    <p className="text-[10px] text-n-text-lt ml-5">{t('approvals_all_posts_reviewed', 'All posts must be reviewed')}</p>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="requiredApproversCount" className="text-xs text-n-text block mb-1">{t('approvals_required_approvers', 'Required Approvers')}</label>
                    <input
                      id="requiredApproversCount"
                      type="number" min={1} max={5}
                      value={workflowSettings.requiredApproversCount}
                      onChange={(e) => setWorkflowSettings({ ...workflowSettings, requiredApproversCount: parseInt(e.target.value) })}
                      className="w-full px-2 py-1.5 border border-n-border rounded-n-sm text-xs bg-n-page text-n-text focus:outline-none focus:ring-1 focus:ring-n-blue"
                    />
                  </div>

                  <div className="mb-3">
                    <p className="text-xs text-n-text mb-2">{t('approvals_notifications', 'Notifications')}</p>
                    <label className="flex items-center cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={workflowSettings.notifyOnSubmission}
                        onChange={(e) => setWorkflowSettings({ ...workflowSettings, notifyOnSubmission: e.target.checked })}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-[11px] text-n-text-md">{t('approvals_notify_submission', 'On submission')}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={workflowSettings.notifyOnApproval}
                        onChange={(e) => setWorkflowSettings({ ...workflowSettings, notifyOnApproval: e.target.checked })}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-[11px] text-n-text-md">{t('approvals_notify_approval', 'On approval')}</span>
                    </label>
                  </div>

                  <button
                    onClick={() => toast.success(t('approvals_settings_updated', 'Workflow settings updated!'))}
                    className="w-full py-1.5 rounded-n-sm text-white text-xs font-semibold transition-all"
                    style={{ backgroundColor: '#2383e2' }}
                  >
                    {t('approvals_save_changes', 'Save Changes')}
                  </button>
                </div>
              )}
            </div>

            {/* Review Activity */}
            <div className="bg-n-card rounded-n-sm border border-n-border shadow-n p-4 flex-shrink-0">
              <h4 className="text-[10px] font-bold text-n-text-lt uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Users size={12} className="text-n-blue" />
                {t('approvals_review_activity', 'Review Activity')}
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between px-2 py-1.5 rounded-n-sm text-[11px]" style={{ backgroundColor: '#dbeddb' }}>
                  <span className="text-n-text-md">{t('approvals_activity_approved', 'Approved')}</span>
                  <span className="font-semibold" style={{ color: '#1c3829' }}>{stats.approved_today}</span>
                </div>
                <div className="flex justify-between px-2 py-1.5 rounded-n-sm text-[11px]" style={{ backgroundColor: '#fdecc8' }}>
                  <span className="text-n-text-md">{t('approvals_activity_pending', 'Pending')}</span>
                  <span className="font-semibold" style={{ color: '#402c1b' }}>{stats.pending}</span>
                </div>
                <div className="flex justify-between px-2 py-1.5 rounded-n-sm text-[11px]" style={{ backgroundColor: '#ffe2dd' }}>
                  <span className="text-n-text-md">{t('approvals_activity_rejection', 'Rejection Rate')}</span>
                  <span className="font-semibold" style={{ color: '#5d1715' }}>{stats.rejection_rate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit for Approval Modal */}
      {showSubmitModal && (
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowSubmitModal(false); }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowSubmitModal(false)}
        >
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.stopPropagation()}
            className="bg-n-card rounded-n p-4 w-[420px] max-h-[70vh] overflow-auto border border-n-border shadow-n"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-n-text mb-1">{t('approvals_modal_title', 'Submit Post for Approval')}</h3>
            <p className="text-xs text-n-text-md mb-3">{t('approvals_modal_description', 'Select a draft post to submit for team review')}</p>

            {drafts.length > 0 ? (
              <div className="space-y-2">
                {drafts.map((draft) => (
                  <div key={draft.id} className="flex items-center justify-between p-2.5 border border-n-border rounded-n-sm hover:bg-n-hover transition-colors">
                    <p className="text-xs text-n-text flex-1 mr-3 line-clamp-2">{draft.text || t('approvals_untitled_draft', 'Untitled Draft')}</p>
                    <button
                      onClick={() => handleSubmitForApproval(draft.id)}
                      disabled={actionLoading === draft.id}
                      className="px-3 py-1.5 rounded-n-sm text-white text-xs font-semibold whitespace-nowrap transition-all disabled:opacity-60"
                      style={{ backgroundColor: '#2383e2' }}
                    >
                      {actionLoading === draft.id ? t('approvals_submitting', 'Submitting...') : t('approvals_submit_button', 'Submit')}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-n-text-md">
                <p className="text-xs">{t('approvals_no_drafts', 'No draft posts available. Create a draft first.')}</p>
              </div>
            )}

            <button
              onClick={() => setShowSubmitModal(false)}
              className="w-full mt-3 py-1.5 bg-n-hover text-n-text-md rounded-n-sm text-xs font-medium hover:bg-n-border transition-colors"
            >
              {t('approvals_cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalsPage;
